import React, { useCallback, useEffect, useState } from "react";

import { useQueries, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { Color } from "Components/Form/Button";
import AccessTab from "Components/Generic/Access/ViewMode";
import Files from "Components/Generic/Files";
import History from "Components/Generic/History";
import Instructions from "Components/Generic/Instructions";
import PackingMaterials from "Components/Generic/PackingMaterials";
import { Navigation } from "Components/Generic/ViewJobComponent";
import { useAppState } from "Context/StateContext";
import { useActionsJob } from "Hooks/useActionJob";
import { Folder } from "Models/folder";
import { Job } from "Models/job";
import { Order } from "Models/order";
import { Recording } from "Models/recording";
import { Task } from "Models/task";
import { User } from "Models/user";
import { useTranslation } from "react-i18next";
import { FiActivity } from "react-icons/fi";
import OrderService from "Services/order.service";
import TaskService from "Services/task.service";
import { ItemJobSubStatus, ResourceName } from "Util/enums";
import { getOrderFolder, getOrderRecordFiles } from "Util/getFolders";
import getItemsToHistory from "Util/getHistory";
import { getOrderCustomerDisplayName } from "Util/names";

import { SkeletonDetails } from "../../../../../../../pages/private/Moving/Operations/Maintenance/Details";
import Details from "../../../../../../../pages/private/Moving/Operations/Maintenance/Details/ViewMode";
import OrderInventory from "../../../../../../../pages/private/Moving/Operations/Maintenance/Inventory";
import TasksTab from "../../../../Operations/Maintenance/Tasks";
import WaybillTab from "../../../../Operations/Maintenance/Waybill";

export const orderViewActions = () => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const [orderToView, setOrderToView] = useState<Job>();
  const [order, setOrder] = useState<Order>();
  const [typeFolder, setTypeFolder] = useState<"Orders" | "Quote">("Orders");
  const [quoteFolders, setQuoteFolders] = useState<Folder[]>([]);

  const handleSetNavigation = useCallback(() => {
    const navList = [
      { key: Navigation.DETAILS },
      { key: Navigation.INVENTORY },
      { key: Navigation.MATERIAL },
      { key: Navigation.TASKS },
      { key: Navigation.ACCESS },
      { key: Navigation.INSTRUCTIONS },
      { key: Navigation.SHIPMENT },
      { key: Navigation.FOLDERS },
      // { key: Navigation.ESTIMATE }, // TODO Camila: wating for estimate screen to be wating
    ];

    return navList;
  }, [order]);

  const { refetch: getOrder, isFetching: loadingOrder } = useQuery(
    ["orders.get", orderToView?.id],
    () =>
      OrderService.getById(Number(orderToView?.id), {
        isSalesApp: true,
        isNewVersion: true,
      }),
    {
      enabled: !!orderToView?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setOrder((currentOrder) =>
            getOrderFolder(translate, { ...currentOrder, ...response.data })
          );
        }
      },
    }
  );

  const getRecording = (setRecord: Function, taskIdVideo: Task[]) => {
    return useQueries({
      queries: taskIdVideo.map((task) => {
        const taskId = task.id;

        return {
          queryKey: ["recording.get", taskId],
          queryFn: () => TaskService.getRecordings(Number(taskId)),
          enabled: !!taskId && !!order,
          refetchOnWindowFocus: false,
          onSuccess: (response: AxiosResponse<Recording[]>) => {
            if (response && response.data) {
              setRecord(response.data, task.startDate);
            }
          },
        };
      }),
    });
  };

  getRecording(
    (records: Recording[], date: string) =>
      getOrderRecordFiles(translate, records, date, quoteFolders, setQuoteFolders),
    order?.tasks?.filter((task) => task.type === "Video Survey") || []
  );

  const { getInventoryList, getJobHistories, getFolders } = useActionsJob({
    jobId: orderToView?.id,
  });

  const { data: historyData } = getJobHistories();

  const { data: foldersData } = getFolders();

  const { data: inventoryData, isLoading: isLoadingInventory } = getInventoryList([
    ItemJobSubStatus.QUOTED,
  ]);

  const { data: actualInventoryData, isLoading: isLoadingActualInventory } = getInventoryList([
    ItemJobSubStatus.ACTUAL,
  ]);

  const getSidebarTitle = (): string => {
    let title = "Order";

    const reference = orderToView?.reference ?? order?.reference;
    const fullName = getOrderCustomerDisplayName(orderToView) || getOrderCustomerDisplayName(order);

    if (reference) title = `${title} #${reference}`;
    if (fullName) {
      title = `${title} - ${fullName}`;
    }

    return title;
  };

  const countMaterial = (): number => {
    let materials = 0;

    order?.inventory?.forEach((inventory) => {
      if (inventory.materials.length > 0) materials += 1;
    });

    return materials;
  };

  const onSwitchFolder = () => {
    setTypeFolder((prev) =>
      prev === ResourceName.ORDERS ? ResourceName.QUOTE : ResourceName.ORDERS
    );
  };

  const handleSetButtonsComponent = useCallback(() => {
    return [
      {
        buttonLabel: translate("seeActivity"),
        titlePage: translate("activity"),
        color: Color.SECONDARY,
        icon: <FiActivity size={16} />,
        renderLoading: () => <SkeletonDetails />,
        renderComponent: () => (
          <History
            items={getItemsToHistory(currentUser ?? ({} as User), order?.id, order?.histories)}
          />
        ),
      },
    ];
  }, [order]);

  const handleSetTabs = useCallback(() => {
    const tabs = [
      {
        key: Navigation.DETAILS,
        loading: loadingOrder,
        isEmpty: !order,
        render: () => <Details order={order} />,
        renderLoading: () => <SkeletonDetails />,
      },
      {
        key: Navigation.INVENTORY,
        loading: loadingOrder || isLoadingInventory || isLoadingActualInventory,
        isEmpty:
          (!order?.inventory && !order?.actualInventory) ||
          (order?.inventory?.length === 0 && order?.actualInventory?.length === 0),
        render: () => (
          <OrderInventory
            order={order}
            inventory={order?.inventory || []}
            quoteJobId={order?.jobId || 0}
            defaultMode={order?.mode || ""}
            destinations={order?.destination?.locations || []}
            actualInventory={order?.actualInventory || []}
            callbackFunction={getOrder}
            storageBoxes={order?.storageBoxes}
            quotedViewOnly
            isOnViewMode
          />
        ),
      },
      {
        key: Navigation.MATERIAL,
        loading: loadingOrder || isLoadingInventory || isLoadingActualInventory,
        isEmpty: !order?.inventory || countMaterial() === 0,
        renderLoading: () => <SkeletonDetails />,
        render: () => <PackingMaterials inventory={order?.inventory} isOnViewMode />,
      },
      {
        key: Navigation.TASKS,
        loading: loadingOrder,
        isEmpty:
          (!order?.tasks && !order?.taskDates) ||
          (order?.tasks?.length === 0 && order?.taskDates?.length === 0),
        render: () => <TasksTab tasks={order?.tasks} taskDates={order?.taskDates} isOnViewMode />,
      },
      {
        key: Navigation.ACCESS,
        loading: loadingOrder,
        isEmpty: !order?.pickupAccess && !order?.deliveryAccess,
        renderLoading: () => <SkeletonDetails />,
        render: () => (
          <AccessTab
            pickupAccess={order?.pickupAccess}
            deliveryAccess={order?.deliveryAccess}
            pickupAddress={order?.origin?.locations?.[0]}
            deliveryAddress={order?.destination?.locations?.[0]}
          />
        ),
      },
      {
        key: Navigation.INSTRUCTIONS,
        loading: loadingOrder,
        isEmpty: !order?.instructions || order?.instructions?.length === 0,
        renderLoading: () => <SkeletonDetails />,
        render: () => <Instructions isOnViewMode instructions={order?.instructions || []} />,
      },
      {
        key: Navigation.SHIPMENT,
        loading: loadingOrder,
        isEmpty: order?.shipments?.length === 0,
        render: () => <WaybillTab waybills={order?.shipments ?? []} isOnViewMode />,
      },
      {
        key: Navigation.FOLDERS,
        render: () => (
          <Files
            isOnViewMode
            inventory={typeFolder === ResourceName.QUOTE ? order?.inventory : undefined}
            folders={typeFolder === ResourceName.QUOTE ? quoteFolders : order?.folders}
            foldersDeleted={order?.foldersDeleted}
            filesDeleted={order?.filesDeleted}
            typeFolder={typeFolder}
            nextTypeFolder={
              typeFolder === ResourceName.ORDERS ? ResourceName.QUOTE : ResourceName.ORDERS
            }
            onSwitchTypeFolder={onSwitchFolder}
          />
        ),
      },
    ];

    return tabs;
  }, [order, typeFolder]);

  useEffect(() => {
    if (inventoryData || actualInventoryData) {
      setOrder((currentOrder) =>
        getOrderFolder(translate, {
          ...currentOrder,
          inventory: inventoryData?.quotedInventory || [],
          actualInventory: actualInventoryData?.actualInventory || [],
        })
      );
    }
  }, [inventoryData, actualInventoryData]);

  useEffect(() => {
    if (historyData && historyData?.length) {
      setOrder((currentOrder) => {
        return { ...currentOrder, histories: historyData };
      });
    }
  }, [historyData]);

  useEffect(() => {
    if (foldersData) {
      setOrder((currentOrder) =>
        getOrderFolder(translate, { ...currentOrder, folders: foldersData })
      );
    }
  }, [foldersData]);

  return {
    getSidebarTitle,
    handleSetNavigation,
    handleSetTabs,
    handleSetButtonsComponent,
    setOrderToView,
    loadingOrder,
  };
};
